import { logout } from "../services/auth";
import { getDenominations as apiGetDenominations } from "../services/api";
// Utility functions for calculations and formatting

// Centralized scratch card price mapping to ensure consistency
export const SCRATCH_CARD_PRICES = {
  3000: 3300,
  5000: 5150,
  10000: 10250,
  15000: 15300,
  30000: 30000,
  60000: 60000,
};

// Centralized E-PIN price mapping to ensure consistency
export const EPIN_PRICES = {
  1000: 1300,
  2000: 2375,
  3000: 3300,
  4000: 4300,
  5000: 5125,
  6000: 6125,
  10000: 10200,
  12000: 12240,
  15000: 15250,
  18000: 18250,
  30000: 30000,
  60000: 60000,
};

let E_TOPUP_MULTIPLIER = 1.025;
export const getETopUpMultiplier = () => E_TOPUP_MULTIPLIER;

export const fetchAndApplyDenominations = async () => {
  try {
    const data = await apiGetDenominations();
    if (!Array.isArray(data)) return;

    data.forEach((item) => {
      const type = String(item.orderType || "").toUpperCase();
      const denom = Number(item.denomination);
      const price = Number(item.price);
      if (!Number.isFinite(price)) return;

      if (type === "EPIN" && Number.isFinite(denom)) {
        EPIN_PRICES[denom] = price;
      } else if (type === "SCRATCH" && Number.isFinite(denom)) {
        SCRATCH_CARD_PRICES[denom] = price;
      } else if (type === "E_TOPUP") {
        E_TOPUP_MULTIPLIER = price;
      }
    });
  } catch (e) {
    console.warn("Failed to fetch denominations; using fallback prices.", e);
  }
};

// Calculate scratch card total with precise calculations
export const calculateScratchCardTotal = (scratchCards) => {
  if (!Array.isArray(scratchCards)) return 0;

  return scratchCards.reduce((total, card) => {
    const price = SCRATCH_CARD_PRICES[card.denomination] || card.price || 0;
    return total + price * card.quantity;
  }, 0);
};

// Calculate E-PIN total with precise calculations
export const calculateEpinTotal = (epins) => {
  if (!Array.isArray(epins)) return 0;

  return epins.reduce((total, epin) => {
    const price = EPIN_PRICES[epin.denomination] || epin.price || 0;
    return total + price * epin.quantity;
  }, 0);
};

// Calculate E-TopUp total with precise calculations
export const calculateETopUpTotal = (eTopUpData) => {
  if (!eTopUpData) return 0;

  const amount = Number(eTopUpData.amount) || 0;
  return amount * getETopUpMultiplier(); // Keep precise, don't round
};

// Calculate grand total for an order with precise calculations
export const calculateOrderGrandTotal = (order) => {
  let total = 0;

  // Add scratch card total
  if (order.scratchCardOrders && order.scratchCardOrders.length > 0) {
    total += calculateScratchCardTotal(order.scratchCardOrders);
  }

  // Add E-PIN total
  if (order.epinOrders && order.epinOrders.length > 0) {
    total += calculateEpinTotal(order.epinOrders);
  }

  // Add E-TopUp total
  if (order.eTopUp || order.etopUpOrder || order.etopUpAmount) {
    total += calculateETopUpTotal(
      order.eTopUp || order.etopUpOrder || { amount: order.etopUpAmount }
    );
  }

  return total;
};

// Get normalized discount details for an order, handling legacy fields
export const getDiscountDetails = (order) => {
  if (!order) return null;

  // If we have full discount details
  if (order.discountDetails) {
    return {
      paidPrice: order.discountDetails.paidPrice,
      paidAmountAfterDiscount: order.discountDetails.paidAmountAfterDiscount,
      discountByAmount: Number(order.discountDetails.discountByAmount || 0),
      discountByPercentage: Number(order.discountDetails.discountByPercentage || 0)
    };
  }

  // NO Fallback for old orders as per user request
  return null;
};

// Calculate the display total for an order, handling discounts
export const calculateOrderTotal = (order) => {
  const details = getDiscountDetails(order);
  if (!details) return null;
  // Make sure we prefer the paidAmountAfterDiscount if available, otherwise paidPrice
  if (details.paidAmountAfterDiscount !== null && details.paidAmountAfterDiscount !== undefined) {
    return Number(details.paidAmountAfterDiscount);
  }
  return Number(details.paidPrice || 0);
};

// Comprehensive calculation validation and debugging
export const validateOrderCalculations = (order) => {
  const results = {
    isValid: true,
    discrepancies: [],
    breakdown: {},
    total: 0,
  };

  // Validate scratch cards
  if (order.scratchCardOrders && order.scratchCardOrders.length > 0) {
    const scratchTotal = calculateScratchCardTotal(order.scratchCardOrders);
    results.breakdown.scratch = {
      total: scratchTotal,
      items: order.scratchCardOrders.map((card) => ({
        denomination: card.denomination,
        price: SCRATCH_CARD_PRICES[card.denomination] || card.price || 0,
        quantity: card.quantity,
        itemTotal:
          (SCRATCH_CARD_PRICES[card.denomination] || card.price || 0) *
          card.quantity,
      })),
    };
    results.total += scratchTotal;

    // Check if backend has scratch amount
    if (
      order.scratchAmountToBePaid &&
      Math.abs(scratchTotal - order.scratchAmountToBePaid) > 0.01
    ) {
      results.discrepancies.push({
        type: "scratch",
        frontend: scratchTotal,
        backend: order.scratchAmountToBePaid,
        difference: scratchTotal - order.scratchAmountToBePaid,
      });
      results.isValid = false;
    }
  }

  // Validate E-PINs
  if (order.epinOrders && order.epinOrders.length > 0) {
    const epinTotal = calculateEpinTotal(order.epinOrders);
    results.breakdown.epin = {
      total: epinTotal,
      items: order.epinOrders.map((epin) => ({
        denomination: epin.denomination,
        price: EPIN_PRICES[epin.denomination] || epin.price || 0,
        quantity: epin.quantity,
        itemTotal:
          (EPIN_PRICES[epin.denomination] || epin.price || 0) * epin.quantity,
      })),
    };
    results.total += epinTotal;

    // Check if backend has epin amount
    if (
      order.epinAmountToBePaid &&
      Math.abs(epinTotal - order.epinAmountToBePaid) > 0.01
    ) {
      results.discrepancies.push({
        type: "epin",
        frontend: epinTotal,
        backend: order.epinAmountToBePaid,
        difference: epinTotal - order.epinAmountToBePaid,
      });
      results.isValid = false;
    }
  }

  // Validate E-TopUp
  if (order.eTopUp || order.etopUpOrder || order.etopUpAmount) {
    const eTopUpTotal = calculateETopUpTotal(
      order.eTopUp || order.etopUpOrder || { amount: order.etopUpAmount }
    );
    results.breakdown.eTopUp = {
      total: eTopUpTotal,
      amount:
        order.eTopUp?.amount || order.etopUpOrder?.amount || order.etopUpAmount,
      multiplier: getETopUpMultiplier(),
    };
    results.total += eTopUpTotal;

    // Check if backend has etopUp amount
    if (
      order.etopUpAmountToBePaid &&
      Math.abs(eTopUpTotal - order.etopUpAmountToBePaid) > 0.01
    ) {
      results.discrepancies.push({
        type: "eTopUp",
        frontend: eTopUpTotal,
        backend: order.etopUpAmountToBePaid,
        difference: eTopUpTotal - order.etopUpAmountToBePaid,
      });
      results.isValid = false;
    }
  }

  // Validate grand total
  if (
    order.totalAmountToBePaid &&
    Math.abs(results.total - order.totalAmountToBePaid) > 0.01
  ) {
    results.discrepancies.push({
      type: "grandTotal",
      frontend: results.total,
      backend: order.totalAmountToBePaid,
      difference: results.total - order.totalAmountToBePaid,
    });
    results.isValid = false;
  }

  return results;
};

// Calculate total for a specific order type
export const calculateTotal = (type, quantities, denominations) => {
  const total = Object.entries(quantities[type]).reduce((sum, [label, qty]) => {
    const denomination = denominations[type].find((d) => d.label === label);
    return sum + denomination.value * qty;
  }, 0);
  // Do not round E-TopUp totals; keep precise
  if (type === "eTopUp") return total;
  return Math.round(total);
};

// Calculate grand total for all order types
export const calculateGrandTotal = (quantities, denominations) => {
  // Sum precise per-type totals; E-TopUp remains unrounded
  return Object.keys(quantities).reduce((sum, type) => {
    return sum + calculateTotal(type, quantities, denominations);
  }, 0);
};

// Utility functions for the application

// Handle API errors and authentication failures
export const handleApiError = (error, navigate) => {
  console.error("API Error:", error);

  const message = error && error.message ? String(error.message) : "";
  let accessToken = null;
  let refreshToken = null;
  try {
    accessToken = localStorage.getItem("accessToken");
    refreshToken = localStorage.getItem("refreshToken");
  } catch {}

  const tokensMissing = !accessToken && !refreshToken;

  if (message && message.includes("JWT expired")) {
    return true;
  }
  const isAuthErrorMessage =
    message &&
    (message.includes("Session expired") ||
      message.includes("Please log in again") ||
      message.includes("Refresh token is invalid") ||
      message.includes("invalid or expired") ||
      message.includes("401") ||
      message.includes("403"));

  if (isAuthErrorMessage || tokensMissing) {
    try {
      logout();
    } catch {
      try {
        localStorage.removeItem("accessToken");
        localStorage.removeItem("refreshToken");
        localStorage.removeItem("user");
      } catch {}
    }

    if (typeof navigate === "function") {
      navigate("/login", { replace: true });
    } else if (typeof window !== "undefined") {
      try {
        window.location.assign("/login");
      } catch {}
    }

    return true;
  }

  return false;
};

// Format currency
export const formatCurrency = (amount) => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(amount);
};

// Format date
export const formatDate = (dateString) => {
  return new Date(dateString).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

// Format date and time
export const formatDateTime = (dateString) => {
  // If dateString ends with 'Z' or has a timezone offset, treat as UTC; otherwise, treat as local
  let date;
  if (/Z$|([+-]\d{2}:?\d{2})$/.test(dateString)) {
    date = new Date(dateString);
  } else if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}$/.test(dateString)) {
    // If it's ISO without timezone, treat as UTC
    date = new Date(dateString + "Z");
  } else {
    date = new Date(dateString);
  }
  return date.toLocaleString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
    timeZone: "Asia/Baghdad", // always use Iraq time
  });
};

// Get status color classes for Tailwind
export const getStatusColor = (status) => {
  switch (status.toLowerCase()) {
    case "pending":
      return "bg-yellow-100 text-yellow-800";
    case "processing":
      return "bg-blue-100 text-blue-800";
    case "completed":
      return "bg-green-100 text-green-800";
    case "rejected":
      return "bg-red-100 text-red-700";
    case "approved1":
      return "bg-blue-100 text-blue-800";
    case "approved2":
      return "bg-purple-200 text-purple-800";
    default:
      return "bg-gray-100 text-gray-800";
  }
};

// Get substatus color classes for Tailwind
export const getSubStatusColor = (subStatus) => {
  if (!subStatus) return "bg-gray-100 text-gray-800";

  switch (subStatus.toUpperCase()) {
    case "IN_PROGRESS":
      return "bg-yellow-100 text-yellow-800";
    case "PREPARED":
      return "bg-blue-100 text-blue-800";
    case "DELIVERED":
      return "bg-green-100 text-green-800";
    default:
      return "bg-gray-100 text-gray-800";
  }
};

// Validate order quantities
export const validateOrderQuantities = (quantities) => {
  const errors = {};

  // Check if there's at least one quantity across all types combined
  const hasAnyQuantity = Object.values(quantities).some((typeQuantities) =>
    Object.values(typeQuantities).some((qty) => qty > 0)
  );

  if (!hasAnyQuantity) {
    errors.general = "Please select at least one quantity for any order type";
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  };
};

// Generate order summary
export const generateOrderSummary = (quantities, denominations) => {
  const summary = [];

  Object.entries(quantities).forEach(([type, typeQuantities]) => {
    const typeTotal = calculateTotal(type, quantities, denominations);
    if (typeTotal > 0) {
      const items = Object.entries(typeQuantities)
        .filter(([_, qty]) => qty > 0)
        .map(([label, qty]) => `${label}: ${qty}`)
        .join(", ");

      summary.push({
        type,
        items,
        total: typeTotal,
      });
    }
  });

  return summary;
};

// Debounce function for search inputs
export const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

// Enhanced search utilities
export const SEARCH_CACHE = new Map();
export const SEARCH_CACHE_TTL = 5 * 60 * 1000; // 5 minutes

// Search cache management
export const searchCache = {
  get: (key) => {
    const cached = SEARCH_CACHE.get(key);
    if (cached && Date.now() - cached.timestamp < SEARCH_CACHE_TTL) {
      return cached.data;
    }
    SEARCH_CACHE.delete(key);
    return null;
  },

  set: (key, data) => {
    SEARCH_CACHE.set(key, {
      data,
      timestamp: Date.now(),
    });
  },

  clear: () => {
    SEARCH_CACHE.clear();
  },

  // Clear expired entries
  cleanup: () => {
    const now = Date.now();
    for (const [key, value] of SEARCH_CACHE.entries()) {
      if (now - value.timestamp >= SEARCH_CACHE_TTL) {
        SEARCH_CACHE.delete(key);
      }
    }
  },
};

// Enhanced debounce with immediate execution for short queries
export const smartDebounce = (func, wait, immediateThreshold = 100) => {
  let timeout;
  return function executedFunction(...args) {
    const query = args[0]?.trim() || "";

    // Execute immediately for very short queries (1-2 characters) or empty queries
    if (query.length <= 2) {
      clearTimeout(timeout);
      func(...args);
      return;
    }

    // Execute immediately for queries that are likely order IDs
    if (isOrderId(query)) {
      clearTimeout(timeout);
      func(...args);
      return;
    }

    // Debounce for longer queries
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

// Enhanced order ID detection
export const isOrderId = (query) => {
  if (!query || typeof query !== "string") return false;

  const trimmed = query.trim();

  // Exact patterns
  if (/^ORD-\d+$/i.test(trimmed)) return true;
  if (/^\d{3,}$/.test(trimmed)) return true; // 3+ digits

  // Partial patterns that might be order IDs
  if (/^ORD/i.test(trimmed)) return true;
  if (/^\d+$/.test(trimmed)) return true;

  return false;
};

// Search result validation
export const validateSearchResult = (result, expectedType = "order") => {
  if (!result) return false;

  switch (expectedType) {
    case "order":
      return result.id && (result.status || result.orderStatus);
    case "user":
      return result.id && (result.name || result.username || result.email);
    default:
      return result.id;
  }
};

// Search error handling with fallback
export const handleSearchError = (error, fallbackData = []) => {
  console.error("Search error:", error);

  // Return fallback data if available
  if (fallbackData && fallbackData.length > 0) {
    console.warn("Using fallback data due to search error");
    return fallbackData;
  }

  // Return empty array as last resort
  return [];
};

// Search query normalization
export const normalizeSearchQuery = (query) => {
  if (!query || typeof query !== "string") return "";

  return query
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ") // Normalize whitespace
    .replace(/[^\w\s-]/g, ""); // Remove special characters except hyphens
};

// Search result deduplication
export const deduplicateSearchResults = (results, key = "id") => {
  if (!Array.isArray(results)) return [];

  const seen = new Set();
  return results.filter((item) => {
    const itemKey = item[key];
    if (seen.has(itemKey)) {
      return false;
    }
    seen.add(itemKey);
    return true;
  });
};

// Search performance monitoring
export const searchMetrics = {
  queries: new Map(),

  record: (query, duration, success) => {
    const normalized = normalizeSearchQuery(query);
    const existing = searchMetrics.queries.get(normalized) || {
      count: 0,
      totalDuration: 0,
      successCount: 0,
      failCount: 0,
    };

    existing.count++;
    existing.totalDuration += duration;
    if (success) {
      existing.successCount++;
    } else {
      existing.failCount++;
    }

    searchMetrics.queries.set(normalized, existing);
  },

  getStats: (query) => {
    const normalized = normalizeSearchQuery(query);
    return searchMetrics.queries.get(normalized);
  },

  clear: () => {
    searchMetrics.queries.clear();
  },
};

// Local storage helpers
export const storage = {
  get: (key) => {
    try {
      const item = localStorage.getItem(key);
      return item ? JSON.parse(item) : null;
    } catch (error) {
      console.error("Error reading from localStorage:", error);
      return null;
    }
  },

  set: (key, value) => {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch (error) {
      console.error("Error writing to localStorage:", error);
    }
  },

  remove: (key) => {
    try {
      localStorage.removeItem(key);
    } catch (error) {
      console.error("Error removing from localStorage:", error);
    }
  },
};
